import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Theme Configuration Interface
 * Defines the structure for whitelabel theme configurations
 */
export interface ThemeConfig {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  cssClass: string;
  logoPath?: string;
  logoPathDark?: string;
  brandColors?: {
    primary: string;
    secondary: string;
  };
}

/**
 * Available Themes
 * Add new themes here as you create them
 */
export const AVAILABLE_THEMES: ThemeConfig[] = [
  {
    id: 'default',
    name: 'default',
    displayName: 'Default Theme',
    description: 'Clean, modern design with green accents',
    cssClass: 'theme-default',
    logoPath: 'assets/logo/default-logo.svg',
    logoPathDark: 'assets/logo/default-logo-dark.svg',
    brandColors: {
      primary: '#059669',
      secondary: '#2563eb'
    }
  },
  {
    id: 'bank',
    name: 'bank',
    displayName: 'Bank Theme',
    description: 'Conservative, trustworthy design for traditional banks',
    cssClass: 'theme-bank',
    logoPath: 'assets/logo/bank-logo.svg',
    logoPathDark: 'assets/logo/bank-logo-dark.svg',
    brandColors: {
      primary: '#1d4ed8',
      secondary: '#374151'
    }
  },
  {
    id: 'fintech',
    name: 'fintech',
    displayName: 'Fintech Theme',
    description: 'Modern, vibrant design for fintech startups',
    cssClass: 'theme-fintech',
    logoPath: 'assets/logo/fintech-logo.svg',
    logoPathDark: 'assets/logo/fintech-logo-dark.svg',
    brandColors: {
      primary: '#9333ea',
      secondary: '#3b82f6'
    }
  }
];

/**
 * Theme Service
 * 
 * Manages runtime theme switching for whitelabel deployment.
 * Themes are applied by adding/removing CSS classes on the body element.
 * 
 * Usage:
 * ```typescript
 * constructor(private themeService: ThemeService) {}
 * 
 * // Set theme
 * this.themeService.setTheme('bank');
 * 
 * // Get current theme
 * this.themeService.currentTheme$.subscribe(theme => {
 *   console.log(theme.displayName);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly STORAGE_KEY = 'app_theme';
  private readonly DEFAULT_THEME_ID = 'default';

  private currentThemeSubject: BehaviorSubject<ThemeConfig>;
  public currentTheme$: Observable<ThemeConfig>;

  constructor() {
    // Load theme from storage or use default
    const savedThemeId = this.getStoredThemeId();
    const initialTheme = savedThemeId 
      ? (this.getThemeById(savedThemeId) || this.getThemeById(this.DEFAULT_THEME_ID)!)
      : this.getThemeById(this.DEFAULT_THEME_ID)!;
    
    this.currentThemeSubject = new BehaviorSubject<ThemeConfig>(initialTheme);
    this.currentTheme$ = this.currentThemeSubject.asObservable();

    // Apply initial theme
    this.applyTheme(initialTheme);
  }

  /**
   * Get all available themes
   */
  public getAvailableThemes(): ThemeConfig[] {
    return AVAILABLE_THEMES;
  }

  /**
   * Get theme configuration by ID
   */
  public getThemeById(themeId: string): ThemeConfig | undefined {
    return AVAILABLE_THEMES.find(theme => theme.id === themeId);
  }

  /**
   * Get current active theme
   */
  public getCurrentTheme(): ThemeConfig {
    return this.currentThemeSubject.value;
  }

  /**
   * Set active theme by ID
   */
  public setTheme(themeId: string): boolean {
    const theme = this.getThemeById(themeId);
    
    if (!theme) {
      console.error(`Theme with id "${themeId}" not found`);
      return false;
    }

    // Remove old theme class
    const currentTheme = this.currentThemeSubject.value;
    if (currentTheme) {
      document.body.classList.remove(currentTheme.cssClass);
    }

    // Apply new theme
    this.applyTheme(theme);
    
    // Update subject
    this.currentThemeSubject.next(theme);
    
    // Persist to storage
    this.saveThemeId(themeId);

    return true;
  }

  /**
   * Set theme by config object (for dynamic themes from API)
   */
  public setThemeByConfig(config: ThemeConfig): void {
    // Remove old theme class
    const currentTheme = this.currentThemeSubject.value;
    if (currentTheme) {
      document.body.classList.remove(currentTheme.cssClass);
    }

    // Apply new theme
    this.applyTheme(config);
    
    // Update subject
    this.currentThemeSubject.next(config);
    
    // Persist to storage
    this.saveThemeId(config.id);
  }

  /**
   * Reset to default theme
   */
  public resetToDefault(): void {
    this.setTheme(this.DEFAULT_THEME_ID);
  }

  /**
   * Check if theme is currently active
   */
  public isThemeActive(themeId: string): boolean {
    return this.currentThemeSubject.value.id === themeId;
  }

  /**
   * Apply theme CSS class to body
   */
  private applyTheme(theme: ThemeConfig): void {
    document.body.classList.add(theme.cssClass);
  }

  /**
   * Get stored theme ID from localStorage
   */
  private getStoredThemeId(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to read theme from localStorage:', error);
      return null;
    }
  }

  /**
   * Save theme ID to localStorage
   */
  private saveThemeId(themeId: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, themeId);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }

  /**
   * Get logo path for current theme
   * @param useDark - Whether to use dark mode logo
   */
  public getLogoPath(useDark = false): string {
    const theme = this.currentThemeSubject.value;
    return useDark ? (theme.logoPathDark || theme.logoPath || '') : (theme.logoPath || '');
  }

  /**
   * Get brand colors for current theme
   */
  public getBrandColors(): { primary: string; secondary: string } | undefined {
    return this.currentThemeSubject.value.brandColors;
  }
}
